import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Variance, variancesApi } from '@/lib/variances';
import { formatMoney } from '@/lib/currency';
import { CommentThread } from './CommentThread';
import { MarkdownText } from './MarkdownText';
import { VarianceNarrative } from './app/VarianceNarrative';

type Filter = 'all' | 'unexplained' | 'unapproved';

export function VariancePanel({ workspaceId }: { workspaceId: string }) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [thresholdPct, setThresholdPct] = useState<number>(5);
  const [filter, setFilter] = useState<Filter>('all');

  const { data: variances = [], isLoading } = useQuery({
    queryKey: ['variances', workspaceId],
    queryFn: () => variancesApi.list(workspaceId),
  });

  const recompute = useMutation({
    mutationFn: () => variancesApi.recompute(workspaceId, thresholdPct),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['variances', workspaceId] }),
  });

  const exportXlsx = useMutation({
    mutationFn: async () => {
      const blob = await variancesApi.exportXlsx(workspaceId);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'cadencez-variances.xlsx';
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    },
  });

  const [openId, setOpenId] = useState<string | null>(null);

  if (isLoading) return (
    <>
      <VarianceNarrative wsId={workspaceId} />
      <div className="text-ink-500 text-sm">{t('variance.loading')}</div>
    </>
  );

  const filtered = variances.filter((v) => {
    if (filter === 'unexplained') return !v.aiExplanation && !v.humanExplanation;
    if (filter === 'unapproved') return !v.approvedAt;
    return true;
  });

  return (
    <>
    <VarianceNarrative wsId={workspaceId} />
    <div className="rounded-2xl border border-ink-200 bg-white p-5">
      <div className="flex items-center justify-between gap-3 mb-3 flex-wrap">
        <h3 className="text-sm font-semibold text-ink-700">
          {t('variance.title')} <span className="num text-ink-500 font-normal">({filtered.length})</span>
        </h3>
        <div className="flex items-center gap-2 flex-wrap">
          {/* Filter pills */}
          <div className="inline-flex rounded-md border border-ink-200 p-0.5 bg-ink-50">
            {(['all', 'unexplained', 'unapproved'] as Filter[]).map((f) => (
              <button
                key={f}
                type="button"
                onClick={() => setFilter(f)}
                className={
                  'px-2.5 py-1 text-[11px] font-semibold rounded transition-colors ' +
                  (f === filter ? 'bg-white text-ink-900 shadow-sm' : 'text-ink-500 hover:text-ink-900')
                }
              >
                {t(`variance.filter_${f}`)}
              </button>
            ))}
          </div>
          {/* Threshold input */}
          <label className="inline-flex items-baseline gap-1.5 text-[11px] text-ink-500">
            <span>{t('variance.threshold_label')}</span>
            <input
              type="number"
              min={0.5}
              max={100}
              step={0.5}
              value={thresholdPct}
              onChange={(e) => setThresholdPct(Number(e.target.value))}
              className="num w-14 rounded-md border border-ink-200 px-1.5 py-1 text-xs text-right"
            />
            <span>%</span>
          </label>
          <button
            onClick={() => recompute.mutate()}
            disabled={recompute.isPending}
            className="px-3 py-1.5 rounded-md border border-ink-300 text-ink-700 hover:bg-ink-100 text-xs"
          >
            {recompute.isPending ? t('variance.recomputing') : t('variance.recompute')}
          </button>
          <button
            onClick={() => exportXlsx.mutate()}
            disabled={exportXlsx.isPending || variances.length === 0}
            className="px-3 py-1.5 rounded-md border border-ink-300 text-ink-700 hover:bg-ink-100 text-xs disabled:opacity-50"
          >
            {exportXlsx.isPending ? t('variance.exporting') : t('variance.export_excel')}
          </button>
        </div>
      </div>

      {filtered.length === 0 ? (
        <p className="text-sm text-ink-500">{t('variance.empty')}</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm min-w-[640px]">
            <thead>
              <tr className="text-left text-ink-500 border-b border-ink-100">
                <th className="py-2 pr-3 font-medium">{t('variance.th_account')}</th>
                <th className="py-2 px-3 font-medium">{t('variance.th_period')}</th>
                <th className="py-2 px-3 font-medium text-right">{t('variance.th_budget')}</th>
                <th className="py-2 px-3 font-medium text-right">{t('variance.th_actual')}</th>
                <th className="py-2 px-3 font-medium text-right">{t('variance.th_variance')}</th>
                <th className="py-2 px-3 font-medium text-right">{t('variance.th_pct')}</th>
                <th className="py-2 px-3 font-medium">{t('variance.th_explanation')}</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((v) => (
                <VarianceRow
                  key={v.id}
                  variance={v}
                  expanded={openId === v.id}
                  onToggle={() => setOpenId((cur) => (cur === v.id ? null : v.id))}
                />
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
    </>
  );
}

function VarianceRow({ variance: v, expanded, onToggle }: { variance: Variance; expanded: boolean; onToggle: () => void }) {
  const { t, i18n } = useTranslation();
  const qc = useQueryClient();

  const refetch = () => qc.invalidateQueries({ queryKey: ['variances', v.workspaceId] });

  const explain = useMutation({ mutationFn: () => variancesApi.explain(v.id), onSuccess: refetch });
  const save = useMutation({
    mutationFn: (humanExplanation: string) => variancesApi.update(v.id, { humanExplanation }),
    onSuccess: refetch,
  });
  const toggleApprove = useMutation({
    mutationFn: () => variancesApi.update(v.id, { approved: !v.approvedAt }),
    onSuccess: refetch,
  });

  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(v.humanExplanation ?? '');

  const isOverBudget = v.varianceAmount > 0;
  const pctColor = v.variancePct !== null && Math.abs(v.variancePct) >= 20 ? 'text-warn-600 font-semibold' : 'text-ink-700';
  const isApproved = !!v.approvedAt;

  return (
    <>
      <tr className="border-b border-ink-50 align-top">
        <td className="py-2 pr-3 num font-medium text-ink-900">{v.accountCode}</td>
        <td className="py-2 px-3 num text-ink-700">{v.periodStart}</td>
        <td className="py-2 px-3 num text-right">{formatMoney(v.budgetAmount, { maximumFractionDigits: 0 })}</td>
        <td className="py-2 px-3 num text-right">{formatMoney(v.actualAmount, { maximumFractionDigits: 0 })}</td>
        <td className={`py-2 px-3 num text-right ${isOverBudget ? 'text-warn-600' : 'text-ink-700'}`}>
          {isOverBudget ? '+' : ''}{formatMoney(v.varianceAmount, { maximumFractionDigits: 0 })}
        </td>
        <td className={`py-2 px-3 num text-right ${pctColor}`}>{v.variancePct?.toFixed(1) ?? '—'}%</td>
        <td className="py-2 px-3 space-x-3 whitespace-nowrap">
          {v.aiExplanation || v.humanExplanation ? (
            <button onClick={onToggle} className="text-accent-600 hover:underline text-xs font-medium">
              {expanded ? t('variance.hide') : t('variance.view')}
            </button>
          ) : (
            <>
              <button
                onClick={() => explain.mutate()}
                disabled={explain.isPending}
                className="text-accent-600 hover:underline text-xs font-medium disabled:opacity-60"
              >
                {explain.isPending ? t('variance.analyzing') : t('variance.ask_ai')}
              </button>
              <button onClick={onToggle} className="text-ink-500 hover:text-ink-900 text-xs">
                {expanded ? t('variance.hide') : t('variance.comments')}
              </button>
            </>
          )}
          {isApproved && <span className="ml-1 text-[10px] text-emerald-700 font-semibold uppercase tracking-wide">✓</span>}
        </td>
      </tr>
      {expanded && (
        <tr>
          <td colSpan={7} className="px-3 pb-4">
            <div className="space-y-3">
              {v.aiExplanation && (
                <div className="rounded-xl bg-ink-50 border border-ink-200 p-4">
                  <div className="text-xs text-emerald-700 font-semibold mb-1">
                    ✦ {t('variance.ai_byline')}
                    {v.aiExplanationCitedFacts.length > 0 && (
                      <span className="ml-2 text-ink-500 font-normal">
                        · {t('variance.cites_n', { count: v.aiExplanationCitedFacts.length })}
                      </span>
                    )}
                  </div>
                  <MarkdownText>{v.aiExplanation}</MarkdownText>
                </div>
              )}

              {/* Human reviewer note: read view + edit-in-place */}
              <div className="rounded-xl bg-white border border-ink-200 p-4">
                <div className="flex items-baseline justify-between mb-1.5">
                  <span className="text-[11px] uppercase tracking-[0.14em] text-ink-500 font-medium">
                    {t('variance.human_note_label')}
                  </span>
                  {!editing && (
                    <button
                      onClick={() => { setDraft(v.humanExplanation ?? ''); setEditing(true); }}
                      className="text-[11px] text-accent-600 hover:underline"
                    >
                      {v.humanExplanation ? t('common.edit') ?? 'Edit' : t('variance.edit_explanation')}
                    </button>
                  )}
                </div>
                {editing ? (
                  <div className="space-y-2">
                    <textarea
                      value={draft}
                      onChange={(e) => setDraft(e.target.value)}
                      placeholder={t('variance.human_note_placeholder')}
                      rows={3}
                      className="w-full rounded-md border border-ink-200 px-3 py-2 text-sm focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/30 outline-none"
                    />
                    <div className="flex items-center gap-2 justify-end">
                      <button
                        onClick={() => setEditing(false)}
                        className="text-xs text-ink-500 hover:text-ink-900 px-2 py-1"
                      >
                        {t('common.cancel') ?? 'Cancel'}
                      </button>
                      <button
                        onClick={() => save.mutate(draft.trim() || null as unknown as string, { onSuccess: () => setEditing(false) })}
                        disabled={save.isPending}
                        className="text-xs font-semibold px-3 py-1 rounded-md bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-60"
                      >
                        {save.isPending ? t('settings.saving') : t('variance.save_note')}
                      </button>
                    </div>
                  </div>
                ) : v.humanExplanation ? (
                  <p className="text-sm text-ink-800 leading-relaxed whitespace-pre-wrap">{v.humanExplanation}</p>
                ) : (
                  <p className="text-xs text-ink-500 italic">{t('variance.human_note_placeholder')}</p>
                )}
              </div>

              {/* Approve / unapprove row */}
              <div className="flex items-center justify-between rounded-xl bg-white border border-ink-200 p-3">
                <div className="text-[11px] text-ink-500">
                  {isApproved
                    ? t('variance.approved_stamp', {
                        at: new Date(v.approvedAt!).toLocaleString(i18n.language),
                      })
                    : ''}
                </div>
                <div className="flex items-center gap-2">
                  <Link
                    to={`/app/workspace/${v.workspaceId}/approvals?targetKind=variance&targetId=${v.id}`}
                    className="text-xs font-semibold px-3 py-1.5 rounded-md border border-ink-300 text-ink-700 hover:bg-ink-100 transition"
                  >
                    {t('approvals.send_for_approval', { defaultValue: 'Send for approval' })}
                  </Link>
                  <button
                    onClick={() => toggleApprove.mutate()}
                    disabled={toggleApprove.isPending}
                    className={
                      'text-xs font-semibold px-3 py-1.5 rounded-md transition ' +
                      (isApproved
                        ? 'border border-ink-300 text-ink-700 hover:bg-ink-100'
                        : 'bg-emerald-600 text-white hover:bg-emerald-700') +
                      ' disabled:opacity-60'
                    }
                  >
                    {isApproved ? t('variance.unapprove') : t('variance.approve')}
                  </button>
                </div>
              </div>

              <div className="rounded-xl bg-white border border-ink-200 p-4">
                <h4 className="text-xs font-semibold text-ink-700 mb-2">{t('variance.comments_label')}</h4>
                <CommentThread workspaceId={v.workspaceId} targetKind="variance" targetId={v.id} />
              </div>
            </div>
          </td>
        </tr>
      )}
    </>
  );
}
